import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, Calendar, ArrowUpRight, ChevronDown } from "lucide-react";
import { business, navLinks, offerings } from "../data/business";
import { useBusinessPhone } from "../hooks/useBusinessPhone";
import { LangSwitch } from "./LangSwitch";

const lodgingSlugs = new Set(["/lodge", "/bunkhouse", "/camping"]);
const diningSlugs = new Set(["/sugarloaf", "/bbq"]);

// Polish round 2 — item #4: thumbnail mega-menu enhancement. Each dropdown
// entry gets a small image and subtitle pulled from the canonical offerings[]
// list in business.ts. We index by slug so there's no duplicated copy and the
// thumbnails stay in sync with the home cards by construction. Subtitles use
// the offering.meta string (e.g. "On-site lodging") because it's short, real,
// and already verified — no fabricated content.
type OfferingItem = (typeof offerings)[number];
const offeringBySlug: Map<string, OfferingItem> = new Map(
  offerings.map((o) => [o.slug as string, o] as const),
);
function thumbForHref(href: string): OfferingItem | undefined {
  const slug = href.replace(/^\//, "");
  return offeringBySlug.get(slug);
}

const desktopGroups = [
  { kind: "link",  href: "/",         label: "Home" },
  { kind: "link",  href: "/rafting",  label: "Rafting" },
  {
    kind: "menu",
    label: "Lodging",
    items: [
      { href: "/lodge",     label: "Ocoee Whitewater Lodge" },
      { href: "/bunkhouse", label: "Bunkhouse" },
      { href: "/camping",   label: "Camping & RV" },
    ],
  },
  {
    kind: "menu",
    label: "Dining",
    items: [
      { href: "/sugarloaf", label: "Sugarloaf Bar & Grill" },
      { href: "/bbq",       label: "Blue Hole BBQ" },
    ],
  },
  { kind: "link", href: "/hikes",   label: "Hikes" },
  { kind: "link", href: "/gallery", label: "Gallery" },
  { kind: "link", href: "/events",  label: "Events" },
  { kind: "link", href: "/contact", label: "Contact" },
] as const;

// Polish round (nav + mobile + perf):
// item #25 — prefetch frequent destinations on first hover/focus. Internal
// routes get a <link rel="prefetch"> hint; the external booking host gets a
// `preconnect` so the TLS handshake is warm by the time the visitor clicks.
// All injected once per URL and cached in module scope so we don't litter
// <head> with dozens of duplicate tags across a session.
const PREFETCHED = new Set<string>();
function injectHint(rel: "prefetch" | "preconnect", href: string) {
  if (typeof document === "undefined") return;
  const key = `${rel}:${href}`;
  if (PREFETCHED.has(key)) return;
  PREFETCHED.add(key);
  const el = document.createElement("link");
  el.rel = rel;
  el.href = href;
  if (rel === "preconnect") el.crossOrigin = "";
  document.head.appendChild(el);
}

export function Nav() {
  const { phone, phoneRaw } = useBusinessPhone();
  const [openPath, setOpenPath] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  // Polish round 2 — item #3: track which desktop dropdown is "open" so the
  // trigger can advertise `aria-expanded` and the chevron rotates. Driven by
  // hover/focus on the parent .nav-menu (matches the existing CSS reveal),
  // not by click — pointer users keep their familiar hover dropdown, keyboard
  // users get focus-within state, and the icon now follows along.
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  // Mirror MobileBottomBar's 480px scroll threshold. When the dock becomes
  // visible it carries the "Book Now" CTA, so the header's mobile BOOK pill
  // would just duplicate it — we fade the header pill out at the same point.
  const [dockVisible, setDockVisible] = useState(false);
  const location = useLocation();
  const open = openPath === location.pathname;

  // Round 2 item #1 (Agent B): body scroll-lock while the drawer is open.
  // Belt-and-braces alongside the implicit close-on-navigation below — even
  // if a future refactor decouples `openPath` from the location, the body
  // never gets stranded in `overflow: hidden`.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Round 2 item #3 (Agent B): explicitly close on route change. The drawer
  // is keyed off `openPath === location.pathname`, so navigating to any new
  // path already implicitly hides it — but this makes the intent local and
  // resilient to refactors that decouple the two states.
  useEffect(() => {
    setOpenPath(null);
  }, [location.pathname]);

  // Focus management — runs only on actual open↔close TRANSITIONS, never
  // on the initial mount. Tracking the previous value via ref skips the
  // first effect call, which previously was force-focusing the burger
  // button on every page load. Visible symptom of that bug: a permanent
  // yellow focus ring around the hamburger that no one ever clicked,
  // plus a screen-reader surprise where the burger captures focus the
  // moment the page lands. Even worse, on iOS Safari, programmatically
  // focusing a button on mount can cause the page to scroll the button
  // into view AND lock the body if the button has `position: fixed` —
  // which contributed to the "scroll seems frozen" report.
  const prevOpenRef = useRef<boolean>(false);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (wasOpen === open) return;
    if (open) {
      const first = document.querySelector<HTMLElement>(".nav-drawer-inner a");
      first?.focus();
    } else if (wasOpen) {
      // Only restore focus to the burger if we're transitioning FROM open.
      const burger = document.querySelector<HTMLElement>(".nav-burger");
      burger?.focus();
    }
  }, [open]);

  // Item #1 + item #3: sticky header with scroll-state class. rAF throttling
  // means we don't recompute scroll math on every native scroll event — the
  // listener just stashes the latest scrollY and waits for the next paint.
  //
  // Hysteresis (jitter fix, 2026-09-19): `.nav` is `position: sticky`, so it
  // still occupies layout space, and `.nav.is-scrolled` shrinks it by ~18-20px
  // (74→56 on phones, 84→64 on desktop). When the header shrinks, everything
  // below moves up and the browser's scroll anchoring compensates by pulling
  // `scrollY` back down — straight through a single 50px threshold, which
  // un-scrolls the header, which grows it, which pushes `scrollY` back up…
  // Measured: 95 class flips in 2.5s from one scrollTo(0, 55) on /bbq. Using a
  // separate exit threshold whose gap (40px) exceeds the height delta breaks
  // the feedback loop without touching the CSS design.
  useEffect(() => {
    const SCROLLED_ENTER = 50;
    const SCROLLED_EXIT = 10;
    let raf = 0;
    let lastY = 0;
    const tick = () => {
      raf = 0;
      setScrolled((prev) =>
        prev ? lastY > SCROLLED_EXIT : lastY > SCROLLED_ENTER,
      );
      setDockVisible(lastY > 480);
    };
    const onScroll = () => {
      lastY = window.scrollY;
      if (!raf) raf = requestAnimationFrame(tick);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Item #6: Escape closes the open drawer. Listener only mounts while the
  // drawer is open so we're not piping every keystroke through React state
  // when nothing is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenPath(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const closeDrawer = useCallback(() => setOpenPath(null), []);

  // Item #6: swipe right-to-left closes the drawer. Threshold of 60px on the
  // X axis with the gesture moving leftward and limited Y drift so a normal
  // vertical scroll inside the drawer is never misread as a dismissal.
  const swipeRef = useRef<{ x: number; y: number } | null>(null);
  const onDrawerPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType !== "touch") return;
    swipeRef.current = { x: e.clientX, y: e.clientY };
  };
  const onDrawerPointerUp = (e: React.PointerEvent) => {
    const start = swipeRef.current;
    swipeRef.current = null;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (dx < -60 && Math.abs(dy) < 80) closeDrawer();
  };

  const isLodgingActive = lodgingSlugs.has(location.pathname);
  const isDiningActive  = diningSlugs.has(location.pathname);

  // Item #25: warm up the booking host on first hover OR focus of any
  // booking CTA. Cheap, idempotent, and only runs on real user intent.
  const onBookHover = () => injectHint("preconnect", "https://go.theflybook.com");
  const onLinkHover = (href: string) => {
    if (href.startsWith("/")) injectHint("prefetch", href);
  };

  return (
    <>
    <header className={`nav ${scrolled ? "is-scrolled" : ""}`}>
      <NavLink
        to="/"
        className="nav-logo"
        end
        aria-label={`${business.name} — Home`}
        onClick={() => setOpenPath(null)}
      >
        <span className="nav-seal" aria-hidden="true">
          <img src="/ocoee-seal-64.png" alt="" />
        </span>
        <span className="nav-wordmark">OCOEE RIVER EXPERIENCE</span>
      </NavLink>

      <nav className="nav-links" aria-label="Primary">
        {desktopGroups.map((g) =>
          g.kind === "link" ? (
            <NavLink
              key={g.href}
              to={g.href}
              end={g.href === "/"}
              className={({ isActive }) => (isActive ? "active" : "")}
              onMouseEnter={() => onLinkHover(g.href)}
              onFocus={() => onLinkHover(g.href)}
            >
              {g.label}
            </NavLink>
          ) : (
            <div
              key={g.label}
              className={`nav-menu ${
                (g.label === "Lodging" && isLodgingActive) ||
                (g.label === "Dining" && isDiningActive)
                  ? "active"
                  : ""
              }`}
              onMouseEnter={() => setOpenMenu(g.label)}
              onMouseLeave={() => setOpenMenu((m) => (m === g.label ? null : m))}
              onFocus={() => setOpenMenu(g.label)}
              onBlur={(e) => {
                // Only close when focus actually leaves the menu subtree.
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  setOpenMenu((m) => (m === g.label ? null : m));
                }
              }}
            >
              <button
                className="nav-menu-trigger"
                type="button"
                aria-expanded={openMenu === g.label}
                aria-haspopup="menu"
              >
                {g.label}{" "}
                {/* item #3 — lucide chevron replaces the bare ▾ glyph so we
                    can rotate a real SVG on aria-expanded for clearer state. */}
                <ChevronDown size={14} aria-hidden="true" className="caret" />
              </button>
              <div className="nav-menu-panel nav-menu-panel-rich" role="menu">
                {g.items.map((it) => {
                  // item #4 — pull a real thumbnail + subtitle from the
                  // canonical offerings list. Falls back gracefully to the
                  // plain link if no card exists for this href yet.
                  const off = thumbForHref(it.href);
                  return (
                    <NavLink
                      key={it.href}
                      to={it.href}
                      role="menuitem"
                      onMouseEnter={() => onLinkHover(it.href)}
                      onFocus={() => onLinkHover(it.href)}
                      className="nav-menu-item"
                    >
                      {off?.img ? (
                        <img
                          className="nav-menu-thumb"
                          src={`/${off.img}`}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          width={48}
                          height={48}
                          aria-hidden="true"
                        />
                      ) : (
                        <span className="nav-menu-thumb nav-menu-thumb-placeholder" aria-hidden="true" />
                      )}
                      <span className="nav-menu-text">
                        <span className="nav-menu-label">{it.label}</span>
                        {off?.meta && (
                          <span className="nav-menu-sub">{off.meta}</span>
                        )}
                      </span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ),
        )}
        <a href={`tel:${phoneRaw}`} className="nav-cta">
          <Phone size={14} aria-hidden="true" /> {phone}
        </a>
        <LangSwitch />
      </nav>

      {/* Persistent Book CTA — visible above-the-fold on mobile, then yields
          to the bottom dock once it slides in (~480px scroll). Avoids two
          identical Book CTAs from competing simultaneously. */}
      <a
        href={business.bookingBase}
        target="_blank"
        rel="noopener noreferrer"
        className={`nav-book-mobile ${dockVisible ? "is-hidden" : ""}`}
        aria-label="Book now (opens in a new tab)"
        aria-hidden={dockVisible}
        tabIndex={dockVisible ? -1 : undefined}
        title="Opens booking site in a new tab"
        onMouseEnter={onBookHover}
        onFocus={onBookHover}
      >
        <Calendar size={14} aria-hidden="true" /> Book
        {/* item #10 — visually hidden hint for screen-reader users */}
        <span className="sr-only">Opens in a new tab</span>
        {/* item #11 — external-link icon consistent across TheFlyBook CTAs */}
        <ArrowUpRight size={12} aria-hidden="true" className="ext-arrow" />
      </a>

      <button
        type="button"
        className="nav-burger"
        aria-label={open ? "Close navigation menu" : "Open navigation menu"}
        aria-expanded={open}
        aria-controls="mobile-drawer"
        onClick={() => setOpenPath((path) => (path === location.pathname ? null : location.pathname))}
      >
        {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
      </button>
    </header>

    {/* Item #15 — clicking the dim overlay outside the inner panel closes
        the drawer. The inner <nav> stops propagation so taps on links don't
        race the overlay handler. */}
    <div
      id="mobile-drawer"
      className={`nav-drawer ${open ? "is-open" : ""}`}
      aria-hidden={!open}
      onClick={(e) => {
        // Only treat clicks that hit the overlay itself (not a child) as
        // outside-clicks. Stops accidental dismissals when the user taps
        // a link inside the panel.
        if (e.target === e.currentTarget) closeDrawer();
      }}
      onPointerDown={onDrawerPointerDown}
      onPointerUp={onDrawerPointerUp}
    >
      <nav
        className="nav-drawer-inner"
        aria-label="Mobile"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Russ's feedback 2026-05-24 (third round): even with the gold
            dot accent, Rafting still read as just another item in the
            flat list. He wants it visually dominant — THE primary
            activity. New treatment: lift Rafting OUT of the flat list
            and render it as a featured CTA card at the very top of
            the drawer. Big gold pill, river icon, "THE OCOEE EXPERIENCE"
            tagline — impossible to miss. Home + the rest of the routes
            follow below as a normal flat list, with Rafting removed
            from the loop (filtered) so it doesn't appear twice. */}
        <NavLink
          to="/rafting"
          className={({ isActive }) =>
            `nav-drawer-feature ${isActive ? "is-active" : ""}`
          }
          onClick={closeDrawer}
          aria-label="Rafting trips — the Ocoee Experience"
        >
          <span className="nav-drawer-feature-tag">THE EXPERIENCE</span>
          <span className="nav-drawer-feature-title">Rafting</span>
          <span className="nav-drawer-feature-sub">
            Class III–IV on the Ocoee &amp; Olympic course
          </span>
        </NavLink>

        {/* Rest of the routes — Rafting omitted (rendered as the feature
            CTA above). */}
        {navLinks
          .filter((l) => l.href !== "/rafting")
          .map((l) => (
            <NavLink
              key={l.href}
              to={l.href}
              end={l.href === "/"}
              className={({ isActive }) => (isActive ? "active" : "")}
              onClick={closeDrawer}
            >
              {l.label}
            </NavLink>
          ))}

        {/* Item #5 — mirror Footer's helpful sections so visitors don't
            have to scroll past every page to reach FAQ / Safety / etc.
            Kept compact (no headings on every group) so the primary nav
            stays the visual anchor. */}
        <div className="nav-drawer-section" aria-label="Helpful">
          <span className="nav-drawer-section-label">HELPFUL</span>
          <NavLink to="/faq" onClick={closeDrawer}>FAQ</NavLink>
          <NavLink to="/what-to-bring" onClick={closeDrawer}>What to Bring</NavLink>
          <NavLink to="/safety" onClick={closeDrawer}>Safety on the Water</NavLink>
          <NavLink to="/cancellation" onClick={closeDrawer}>Cancellation</NavLink>
          <NavLink to="/waiver" onClick={closeDrawer}>Waiver</NavLink>
        </div>

        <a
          href={`tel:${phoneRaw}`}
          className="btn btn-gold"
          style={{ marginTop: 18 }}
          onClick={closeDrawer}
        >
          <Phone size={16} aria-hidden="true" style={{ marginRight: 8 }} /> Call {phone}
        </a>
        <LangSwitch />
      </nav>
    </div>
    </>
  );
}

// Item #2 — "BOOKING…" state when the user is on the external booking host.
// Skipped: the Book CTA opens go.theflybook.com in a NEW TAB, so the SPA
// can't observe its load state from inside our origin (cross-origin tab,
// no iframe). Reintroduce only if/when the booking flow is embedded.
